"use client";

import { useEffect, useRef } from "react";
import { useDebounce } from "@/lib/hooks/debounce";
import { CartSyncMessage } from "@/lib/types/boadcast/message";
import { CartSyncReason } from "@/lib/types/boadcast/reasons";
import {encryptSessionData} from "@/lib/functions/encryptSessionData";
import {decryptSessionData} from "@/lib/functions/decryptSessionData";

// Shared BroadcastChannel helpers for cross-tab cart sync.

const BROADCAST_CHANNEL_NAME = `cart-sync:${process.env.NEXT_PUBLIC_SYLIUS_CHANNEL_CODE ?? "SPECS"}`;
const STORAGE_KEY = `${BROADCAST_CHANNEL_NAME}:storage`;

// eslint-disable-next-line no-undef
let channel: BroadcastChannel | null = null;
let sourceId: string | null = null;

const isSupported = () => typeof globalThis !== "undefined" && "BroadcastChannel" in globalThis;

const getSourceId = () => {
  if (!sourceId) {
    const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
    if (cryptoObj?.randomUUID) {
      sourceId = cryptoObj.randomUUID();
    } else {
      sourceId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
  }
  return sourceId;
};

const getChannel = () => {
  if (!isSupported()) return null;
  if (!channel) channel = new globalThis.BroadcastChannel(BROADCAST_CHANNEL_NAME);
  return channel;
};

export const publishCartRefresh = (
  token?: string | null,
  type?: CartSyncReason | undefined,
) => {
  const message: CartSyncMessage = {
    type: type ?? "cart_refreshed",
    sourceId: getSourceId(),
    token: token ?? null,
    timestamp: Date.now(),
  };

  const activeChannel = getChannel();
  if (activeChannel) {
    activeChannel.postMessage(message);
  }

  try {
    if (typeof globalThis !== "undefined" && "localStorage" in globalThis) {
      globalThis.localStorage.setItem(STORAGE_KEY, encryptSessionData(JSON.stringify(message)));
    }
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }

  return !!activeChannel;
};

export const useSubscribeToCartRefresh = (
  handler: (message: CartSyncMessage) => void,
  debounceMs = 300
) => {
  const handlerRef = useRef(handler);
  const debounce = useDebounce(debounceMs);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const activeChannel = getChannel();
    // eslint-disable-next-line no-undef
    const onMessage = (event: MessageEvent<CartSyncMessage>) => {
      const data = event.data;
      if (!data) return;
      if (data.sourceId === getSourceId()) return;
      debounce(() => handlerRef.current(data));
    };

    // eslint-disable-next-line no-undef
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return;
      try {
        const data = JSON.parse(decryptSessionData(event.newValue)) as CartSyncMessage;
        if (!data) return;
        if (data.sourceId === getSourceId()) return;
        debounce(() => handlerRef.current(data));
      } catch {
        // Ignore malformed storage payloads.
      }
    };

    if (activeChannel) {
      activeChannel.addEventListener("message", onMessage);
    }

    if (typeof globalThis !== "undefined") {
      globalThis.addEventListener("storage", onStorage);
    }

    return () => {
      if (activeChannel) {
        activeChannel.removeEventListener("message", onMessage);
      }
      if (typeof globalThis !== "undefined") {
        globalThis.removeEventListener("storage", onStorage);
      }
    };
  }, [debounce]);
};
